import {
  type CSSProperties,
  type Ref,
  type RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ClaimKind,
  ClaimLineEdit,
  ClaimLineResponse,
} from "@patent-studio/api-types";
import {
  StructuredText,
  type StructuredTextHandle,
  type TextBlock,
} from "@/components/ui/StructuredText";
import { NotSavedNote } from "@/components/ui/Note";
import type { StructuredTextFeatures } from "@/lib/structured-text";
import { useCommittedDraft } from "@/hooks/use-committed-draft";
import { asBlocks, asDocument, textOf } from "@/lib/structured-text";
import { cn } from "@/lib/utils";
import {
  CLAIM_CONTROLS,
  CLAIM_SHORTCUTS,
  DEEPEST_LINE,
  MOST_LINES,
} from "./claim-controls";
import { claimLineStyle } from "./claim-line-style";
import { claimMarkers, type ClaimMarkerScheme } from "./claim-markers";
import { useDrawnElements } from "./drawn-elements";

/** What an empty claim shows to be written in; stored only once it holds words. */
const FIRST_LINE: ClaimLineEdit = { runs: [], depth: 0, numbered: false };

/**
 * A stored line as the editor holds it. The runs are optional on the wire and
 * never here, and a run's recital is what its words state.
 */
function linesToBlocks(lines: readonly ClaimLineEdit[]): TextBlock[] {
  return lines.map((line) => ({
    runs: (line.runs ?? []).map(({ reference, recital, text }) => ({
      reference: reference ?? null,
      ...(recital === undefined || recital === null ? {} : { stated: recital }),
      text,
    })),
    depth: line.depth,
    numbered: line.numbered,
  }));
}

/**
 * The editor's blocks as the lines a save sends. A run keeps the recital its
 * words state, and a run that states nothing sends none.
 */
function blocksToLines(blocks: readonly TextBlock[]): ClaimLineEdit[] {
  return blocks.map(({ runs, depth, numbered }) => ({
    runs: runs.map(({ reference, stated, text }) => ({
      reference,
      ...(stated === undefined ? {} : { recital: stated }),
      text,
    })),
    depth,
    numbered,
  }));
}

/** A line with nothing written in it is not a line, so it is never sent as one. */
function withWords(lines: readonly ClaimLineEdit[]): ClaimLineEdit[] {
  return lines.filter(
    (line) => textOf({ runs: line.runs ?? [] }).trim() !== "",
  );
}

/** A custom property beside the ordinary ones, which React's style type does not describe. */
type LeadInWidth = CSSProperties & Record<"--claim-lead-in", string>;

/**
 * A claim's words, written as the structure they are.
 *
 * One editor for the whole claim rather than a field for each line. A claim is
 * one piece of prose whose lines are its shape, and a column of separate fields
 * made that shape the writer's job: Enter had to be caught and the focus moved
 * by hand, and a stretch of words could not say what it named, because a field
 * of plain text has nowhere to hold it.
 *
 * The markers are projected here rather than taken from what was served. The
 * server's marker is right for the words the server last saw and goes stale the
 * moment a line is numbered, so the rule is restated over the words as they now
 * stand, and both sides are held to one fixture.
 */
export function ClaimLines({
  kind,
  lines,
  scheme,
  leadIn,
  scope,
  onSave,
  editorRef,
}: {
  kind: ClaimKind;
  lines: readonly ClaimLineResponse[];
  /** How the matter spells its markers, which is one setting for the whole set. */
  scheme: ClaimMarkerScheme;
  /** A dependent claim's opening words, drawn before its first line and never stored. */
  leadIn?: string;
  /** Names the stage whose acts send these words before they read them. */
  scope?: string;
  onSave: (lines: ClaimLineEdit[]) => Promise<void>;
  /** For the claim's Edit text, which puts the caret in these words. */
  editorRef?: Ref<StructuredTextHandle>;
}) {
  // The served lines as the editor hands them back when nothing was typed,
  // serialised once per served set rather than once per render: the whole
  // claim set re-renders on every step of a drag. The draft is a string because
  // that is what the stage's flush holds, and the lock waits on it.
  //
  // In the editor's own shape, so the draft is compared with words of the same
  // shape. The server serves every run with a recital key and the editor joins
  // neighbouring plain runs, so the lines as served never equal a draft. The
  // attorney's own save would then never be adopted, and a later write from
  // elsewhere would be shown nowhere and written over by the next flush. A
  // click in and out would save as well.
  const stored = useMemo(
    () =>
      JSON.stringify(
        withWords(blocksToLines(asBlocks(asDocument(linesToBlocks(lines))))),
      ),
    [lines],
  );
  const { draft, setDraft, commit, failed, retry } = useCommittedDraft(
    stored,
    async (value) => {
      const worded = withWords(JSON.parse(value) as ClaimLineEdit[]);
      if (JSON.stringify(worded) !== stored) await onSave(worded);
    },
    scope,
  );
  const blocks = useMemo(() => {
    const written = JSON.parse(draft) as ClaimLineEdit[];
    return linesToBlocks(written.length === 0 ? [FIRST_LINE] : written);
  }, [draft]);
  const [full, setFull] = useState(false);
  const drawn = useDrawnElements();
  const { rowRef, leadRef, natural, stacked } = useLeadInFit(leadIn);

  return (
    <div
      ref={rowRef}
      className="group/lines relative"
      aria-keyshortcuts="Tab Shift+Tab Enter Escape"
      // The lead-in sits on the first line's first row and the words wrap under
      // it, as one sentence reads. In a column too narrow to hold it on one row
      // it sits above them, and nothing is indented. Set here rather than on the
      // editor because a custom property is inherited, and the editor holds only
      // what every consumer of it holds.
      style={
        !stacked && natural > 0
          ? ({ "--claim-lead-in": `${String(natural)}px` } as LeadInWidth)
          : undefined
      }
    >
      {leadIn !== undefined && (
        <LeadIn
          words={leadIn}
          stacked={stacked}
          span={leadRef}
          className={claimLineStyle(kind, 0)}
        />
      )}
      <StructuredText
        ref={editorRef}
        label="Claim"
        value={blocks}
        onChange={(next) => {
          setDraft(JSON.stringify(blocksToLines(next)));
          setFull(false);
        }}
        onLeave={(next) => {
          commit(JSON.stringify(blocksToLines(next)));
          // The refusal belongs to the press that was refused, not to the claim.
          // Left standing it replaces the keyboard hint for good, and the next
          // attorney to write in this claim is told it is full when it is not.
          setFull(false);
        }}
        className="[&_.ProseMirror>p:first-child]:[text-indent:var(--claim-lead-in,0px)]"
        features={writing(kind, full, setFull, scheme, drawn)}
      />
      <NotSavedNote failed={failed} onRetry={retry} className="mt-1" />
    </div>
  );
}

/** What the claim editor does beyond holding words, which is the same for every claim. */
function writing(
  kind: ClaimKind,
  full: boolean,
  setFull: (full: boolean) => void,
  scheme: ClaimMarkerScheme,
  drawn: ReturnType<typeof useDrawnElements>,
): StructuredTextFeatures {
  return {
    references: drawn.references,
    // A claim has one preamble, so the line after it is an element.
    nesting: {
      deepest: DEEPEST_LINE,
      under: (block) => Math.max(1, block.depth),
    },
    controls: {
      acts: CLAIM_CONTROLS,
      // In the bar rather than under the words. A line of its own below them sat
      // over the claim's own controls, and reserving room for it would push every
      // claim down the page as one was clicked into.
      note: full
        ? `A claim holds ${String(MOST_LINES)} lines. The rest belong in another claim.`
        : CLAIM_SHORTCUTS,
    },
    blockMarkers: (blocks) => claimMarkers(blocks, scheme),
    // Both reasons a drawn claim goes out of date, in the one value the seam
    // takes. What a numeral shows moves with the drawings and not with the
    // words, so pinning an element on a sheet has to reach every claim citing
    // it without rewriting one; and a matter respelled while a claim is open
    // redraws its markers under the caret rather than at the next keystroke.
    // Either alone would leave the other changing nothing on the screen.
    redrawOn: `${scheme}:${String(drawn.version)}`,
    blockClass: (block) => claimLineStyle(kind, block.depth),
    capacity: {
      blocks: MOST_LINES,
      refused: () => {
        setFull(true);
      },
    },
  };
}

/**
 * A dependent's opening words, drawn before its first line and never stored.
 *
 * Laid over the words where it fits, so they wrap under it as one sentence
 * reads. Where it does not fit it takes a row of its own, and the words start
 * under it unindented.
 */
function LeadIn({
  words,
  stacked,
  span,
  className,
}: {
  words: string;
  stacked: boolean;
  span: RefObject<HTMLSpanElement | null>;
  /** The type its claim is set in, so it reads as one sentence with the line it opens. */
  className: string;
}) {
  return (
    <span
      ref={span}
      className={cn(
        className,
        stacked
          ? "block font-bold"
          : "pointer-events-none absolute top-px left-0 z-[1] pr-1 font-bold whitespace-nowrap",
      )}
    >
      {words}
    </span>
  );
}

/** The room a first word needs beside the lead-in, in pixels: a long claim word at the claim's size. */
const FIRST_WORD_ROOM = 160;

/**
 * Whether the lead-in fits on the first row beside the words, and how wide it
 * is when it does.
 *
 * Measured after the layout rather than while the tree is built: a ref callback
 * written inline is a new function on every render, so React tears the observer
 * down and builds another one each time, and reading a width during the commit
 * makes the browser lay the row out again to answer.
 */
function useLeadInFit(leadIn: string | undefined) {
  const [natural, setNatural] = useState(0);
  const [room, setRoom] = useState(0);
  const rowRef = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLSpanElement>(null);
  // The first word starts the line beside the lead-in and cannot wrap before
  // it, so a row with less room than a word left beside the lead-in would split
  // that word: the lead-in goes above instead.
  const stacked = room > 0 && natural > room - FIRST_WORD_ROOM;
  // Read by the measuring below without re-subscribing it: a stacked lead-in
  // has the whole row, so measuring it then would report the row's width as its
  // natural one and it could never come back down.
  const stackedRef = useRef(stacked);
  stackedRef.current = stacked;
  useLayoutEffect(() => {
    const row = rowRef.current;
    const lead = leadRef.current;
    if (row === null || lead === null || leadIn === undefined) return undefined;
    const measure = () => {
      setRoom(row.clientWidth);
      // Both read 0 while the card holding the row is folded, and the row is
      // built folded. Measuring once on mount would keep that 0 for good, so
      // the observer answers again when the fold opens and the row gets a size.
      if (!stackedRef.current && lead.offsetWidth > 0)
        setNatural(lead.offsetWidth);
    };
    // Observing delivers the current size straight away, so nothing is measured
    // here: a set of state during the effect itself renders the row twice.
    const watch = new ResizeObserver(measure);
    watch.observe(row);
    watch.observe(lead);
    return () => {
      watch.disconnect();
    };
  }, [leadIn]);
  return { rowRef, leadRef, natural, stacked };
}
